import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { erroreDi, giorno, giornoCorto } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CampoData from "@/components/common/CampoData";
import { quando } from "./Assistenze";

const GIORNO = 86400000;

/** Giorni di ritardo di una scaduta: data prevista contro l'«oggi» che dà il server. */
function giorniDiRitardo(a, oggi) {
  if (!a.scaduta || !oggi) return 0;
  return Math.max(1, Math.round((Date.parse(oggi) - Date.parse(a.data_prevista)) / GIORNO));
}

const giorniTesto = (n) => `${n} ${n === 1 ? "giorno" : "giorni"}`;

/** Il segno di stato: la scaduta vince sullo stato che ha scritto chi l'ha aperta. */
function segnoDi(a, oggi) {
  if (a.stato === "fatta") return { tono: "success", testo: "Fatta" };
  if (a.scaduta) return { tono: "danger", testo: `Scaduta da ${giorniTesto(giorniDiRitardo(a, oggi))}` };
  if (a.stato === "programmata") return { tono: "brand", testo: "Programmata" };
  return { tono: "warning", testo: "Da fare" };
}

/** «Quando», in una riga di elenco. */
export function quandoRiga(a, oggi) {
  if (a.stato === "fatta") return `fatta il ${giornoCorto(a.data_chiusura)}`;
  if (a.scaduta) return `scaduta da ${giorniTesto(giorniDiRitardo(a, oggi))}`;
  return quando(a.data_prevista);
}

/** «Da fissare» · «Fatta il gio 29 ott» · «domani, ven 1 nov». */
function quandoLungo(a) {
  if (a.stato === "fatta") return `Fatta il ${giorno(a.data_chiusura)}`;
  if (!a.data_prevista) return "Da fissare";
  const q = quando(a.data_prevista);
  return q === giorno(a.data_prevista) ? q : `${q}, ${giorno(a.data_prevista)}`;
}

/** Fissa o sposta la data: una data sola, e l'assistenza diventa «programmata». */
function SpostaDialog({ a, aperto, onChiudi, onFatto }) {
  const [data, setData] = useState(a.data_prevista || "");
  const [busy, setBusy] = useState(false);
  const fissa = !a.data_prevista;
  const salva = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/assistenze/${a.id}`, { data_prevista: data, stato: "programmata" });
      onChiudi();
      onFatto(fissa ? "Intervento fissato" : "Intervento spostato");
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={aperto} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent data-testid="assistenza-sposta-modal">
        <form onSubmit={salva} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>{fissa ? "Fissa l'intervento" : "Sposta l'intervento"}</DialogTitle>
            <DialogDescription>
              {a.cliente}: {a.descrizione}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-inside">
            <Label htmlFor="as-sposta">{fissa ? "Quando passate?" : "Nuova data"}</Label>
            <CampoData id="as-sposta" value={data} onChange={(e) => setData(e.target.value)} testId="assistenza-sposta-data" required />
          </div>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="secondary" onClick={onChiudi}>
              Annulla
            </Button>
            <Button type="submit" disabled={busy || !data} data-testid="assistenza-sposta-salva">
              {busy ? "Salvo…" : fissa ? "Fissa" : "Sposta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Riga({ nome, children }) {
  return (
    <>
      <dt className="text-ink-2">{nome}</dt>
      <dd className="font-semibold text-ink-strong">{children}</dd>
    </>
  );
}

/**
 * Il dettaglio di un'assistenza, accanto all'elenco (sotto i 1024 px prende il posto
 * dell'elenco, con «Assistenza» per tornare). Cosa si può fare lo decide `modificabile`
 * (il responsabile di sede legge le assistenze dei colleghi, non le cambia) e il ruolo:
 * il montatore aggiorna le sue, non le riassegna.
 */
export default function DettaglioAssistenza({ a, oggi, montatore, onFatto, onModifica, onIndietro }) {
  const [sposta, setSposta] = useState(false);
  const [busy, setBusy] = useState(false);
  const segno = segnoDi(a, oggi);
  const apribile = a.modificabile !== false;
  const daFissare = a.stato !== "fatta" && !a.data_prevista;

  const segnaFatta = async () => {
    setBusy(true);
    try {
      await api.patch(`/assistenze/${a.id}`, { stato: "fatta" });
      onFatto("Assistenza segnata come fatta");
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="grid gap-card rounded-lg border border-line bg-surface p-card" data-testid="assistenza-dettaglio">
      {onIndietro ? (
        <Button variant="ghost" size="sm" className="-ml-inside justify-self-start" onClick={onIndietro} data-testid="assistenza-indietro">
          <ChevronLeft />
          Assistenza
        </Button>
      ) : null}

      <header className="grid gap-inside">
        <StatusBadge tone={segno.tono} className="justify-self-start" data-testid="assistenza-segno">
          {segno.testo}
        </StatusBadge>
        <h2 className="font-display text-h3 text-ink-strong">{a.descrizione}</h2>
        <p className="text-body text-ink-2">
          {montatore ? (
            a.cliente
          ) : (
            <Link
              to={`/clienti/${a.cliente_id}`}
              className="inline-flex min-h-11 items-center rounded-xs font-semibold text-brand-ink underline md:min-h-0 underline-offset-[3px] hover:decoration-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              data-testid="assistenza-scheda"
            >
              {a.cliente}
            </Link>
          )}
          {a.sede ? ` · ${a.sede}` : ""}
        </p>
      </header>

      {apribile ? (
        <div className="flex flex-wrap gap-inside" data-testid="assistenza-azioni">
          {a.stato === "fatta" ? null : (
            <>
              {daFissare ? (
                <Button onClick={() => setSposta(true)} data-testid="assistenza-fissa">
                  Fissa l'intervento
                </Button>
              ) : null}
              <Button
                variant={daFissare ? "secondary" : "primary"}
                onClick={segnaFatta}
                disabled={busy}
                data-testid="assistenza-segna-fatta"
              >
                Segna fatta
              </Button>
              {daFissare ? null : (
                <Button variant="secondary" onClick={() => setSposta(true)} data-testid="assistenza-sposta">
                  Sposta
                </Button>
              )}
            </>
          )}
          <Button variant="secondary" onClick={() => onModifica(a)} data-testid="assistenza-modifica">
            Modifica
          </Button>
        </div>
      ) : (
        <p className="rounded-md bg-surface-2 p-group text-body text-ink-2" data-testid="assistenza-sola-lettura">
          Il cliente lo segue un collega: la vedi, la cambia chi lo segue.
        </p>
      )}

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-card gap-y-inside text-body">
        <Riga nome="Quando">{quandoLungo(a)}</Riga>
        <Riga nome="Dove">{a.luogo || "Da indicare"}</Riga>
        <Riga nome="Chi la fa">{a.assegnato || "Da assegnare"}</Riga>
        <Riga nome="Aperta il">{giorno(a.data_apertura)}</Riga>
      </dl>

      {apribile ? <SpostaDialog key={`${a.id}-${a.data_prevista}`} a={a} aperto={sposta} onChiudi={() => setSposta(false)} onFatto={onFatto} /> : null}
    </article>
  );
}
